import { Listing } from 'components/dropdown'
import { store } from 'config/user/store'
import { ZodError, ZodType, z } from 'zod'

export interface DropdownProps {
  value?: string | number | null
  options?: Listing[] | undefined
}

export interface MultiDropDownProps {
  value: (string | number)[]
  options?: Listing[] | undefined
}

export const formatDate = (dateString: string): string => {
  const options: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
  }
  const date = new Date(dateString)
  return date.toLocaleDateString(undefined, options)
}

export const getValidationErrors = (
  error: z.ZodError
): Record<string, string> => {
  const errors: Record<string, string> = {}

  for (const errorItem of error.errors) {
    if (errorItem.path) {
      errors[errorItem.path.join('.')] = errorItem.message
    }
  }

  return errors
}

type Resolver<T> = (
  data: T
) => Promise<{ values: T; errors: Record<string, string> }>

export const createResolver =
  <T>(schema: ZodType<T, any, any>): Resolver<T> =>
  async data => {
    try {
      const validatedData = schema.parse(data)
      return {
        values: validatedData,
        errors: {},
      }
    } catch (error) {
      const validationErrors = getValidationErrors(error as ZodError)
      return {
        values: {} as T,
        errors: validationErrors,
      }
    }
  }

export const updateMultiSelectDropdownValue = ({
  value,
  options,
}: MultiDropDownProps) => {
  const multiSelect = options
    ?.filter(item => {
      return value?.includes(item?.value) && item
    })
    .sort((a, b) => {
      return value.indexOf(a.value) - value.indexOf(b.value)
    })

  return multiSelect
}

export const isErrorStatusCode = (statusCode?: string): boolean => {
  const code = parseInt(statusCode || '', 10)
  return !isNaN(code) && code >= 400
}

export const hexToHSL = (hex: string) => {
  if (!hex) return null
  let r = parseInt(hex.slice(1, 3), 16)
  let g = parseInt(hex.slice(3, 5), 16)
  let b = parseInt(hex.slice(5, 7), 16)
  r /= 255
  g /= 255
  b /= 255
  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)
  let h = 0,
    s,
    l = (max + min) / 2
  if (max === min) {
    s = 0
  } else {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0)
        break
      case g:
        h = (b - r) / d + 2
        break
      case b:
        h = (r - g) / d + 4
        break
    }
    h /= 6
  }
  return { h: h * 360, s: s * 100, l: l * 100 }
}

export const createHSLVariants = (hex: string) => {
  const hsl = hexToHSL(hex)
  if (!hsl) return []
  const { h, s, l } = hsl
  const variants = []
  const lightVariants = 7
  const darkVariants = 4

  for (let i = 1; i <= lightVariants; i++) {
    let lightness = l + (i * (100 - l)) / (lightVariants + 1)
    variants.push(
      `hsl(${h.toFixed(2)}, ${s.toFixed(2)}%, ${lightness.toFixed(2)}%)`
    )
  }

  variants.push(`hsl(${h.toFixed(2)}, ${s.toFixed(2)}%, ${l.toFixed(2)}%)`)

  for (let i = 1; i <= darkVariants; i++) {
    let lightness = l - (i * l) / (darkVariants + 1)
    variants.unshift(
      `hsl(${h.toFixed(2)}, ${s.toFixed(2)}%, ${lightness.toFixed(2)}%)`
    )
  }
  return variants
}

export const getImageName = (url: string): string | undefined => {
  const parts: string[] = url.split('--')
  return parts.pop()
}

export const imageUrlToBlob = (url: string) => {
  return fetch(url)
    .then(response => response.blob())
    .then(
      blob =>
        new Promise((resolve, reject) => {
          const fileName = getImageName(url)
          const file = new File([blob], fileName || '', { type: blob.type })
          resolve(file)
        })
    )
}

export const getServiceSecret = (): string => {
  const environmentState = store.getState().themeSetting.environment
  const mode = environmentState.mode
  if (mode === 'localhost' || mode === 'dev-inspeq') {
    return 'kranksecret'
  }
  return 'PG4^MjSt:/&}Dd$HVZ6`+.'
}

export const getLocalizedTitle = (data: Record<string, any>, lang: string) => {
  const fallbackLang = 'en'
  const key = `title_${lang}`
  if (data[key]) return data[key]
  if (data[`title_${fallbackLang}`]) return data[`title_${fallbackLang}`]
  return data.title || ''
}
